import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardCheck, Search, ArrowRight, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { CATEGORY_LABELS, CATEGORY_STYLES, MODULE_LABELS } from "@/lib/strings";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AskEvaButton } from "@/components/AskEvaButton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function CompletionBar({ value, testid }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2.5 rounded-full bg-slate-100 border border-slate-200" data-testid={testid}>
        <div className="h-full rounded-full bg-green-700 transition-[width] duration-300" style={{ width: `${value}%` }} />
      </div>
      <span className="text-sm font-bold text-slate-900 w-12 text-right">{value}%</span>
    </div>
  );
}

export default function Vsme() {
  const { companyId, period, periods, changePeriod, can } = useTenant();
  const [overview, setOverview] = useState(null);
  const [filters, setFilters] = useState({ module: "ALL", category: "ALL", status: "ALL", q: "" });

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/${period.id}/vsme`);
      setOverview(data);
    } catch (err) {
      toast.error("Errore caricamento VSME", { description: formatApiError(err) });
    }
  }, [companyId, period?.id]);

  useEffect(() => { load(); }, [load]);

  const changeOption = async (option) => {
    try {
      await api.put(`/companies/${companyId}/periods/${period.id}/vsme/options`, { reporting_option: option });
      toast.success("Opzione di rendicontazione aggiornata");
      load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const filtered = useMemo(() => {
    if (!overview) return [];
    return overview.disclosures.filter((d) => {
      if (filters.module !== "ALL" && d.module !== filters.module) return false;
      if (filters.category !== "ALL" && d.category !== filters.category) return false;
      if (filters.q) {
        const q = filters.q.toLowerCase();
        if (!(`${d.code} ${d.title}`.toLowerCase().includes(q))) return false;
      }
      if (filters.status === "MISSING" && d.missing === 0) return false;
      if (filters.status === "TO_VERIFY" && d.to_verify === 0) return false;
      if (filters.status === "COMPLETE" && !(d.applicable > 0 && d.missing === 0)) return false;
      return true;
    });
  }, [overview, filters]);

  if (!period) {
    return (
      <div className="max-w-xl" data-testid="vsme-no-period">
        <Card className="border-slate-200"><CardContent className="p-6 text-sm text-slate-600">
          Nessun periodo di rendicontazione disponibile. Crea un periodo dalla sezione "Periodi di rendicontazione".
        </CardContent></Card>
      </div>
    );
  }

  const basic = overview?.modules?.BASIC;
  const comp = overview?.modules?.COMPREHENSIVE;

  return (
    <div className="space-y-6" data-testid="vsme-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <ClipboardCheck className="w-7 h-7 text-green-700" aria-hidden="true" />
            VSME
          </h1>
          <AskEvaButton question="Qual è la cosa più veloce che posso completare adesso nel VSME?" />
          </div>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl leading-relaxed">
            {overview?.standard_version?.standard_name} — {overview?.standard_version?.version_name}
            {overview?.standard_version?.version_date && ` (${overview.standard_version.version_date})`}
          </p>
        </div>
        <Badge variant="outline" className="bg-green-50 text-green-800 border-green-300" data-testid="vsme-version-badge">
          Versione: {overview?.standard_version?.status === "ACTIVE" ? "ATTIVA" : overview?.standard_version?.status}
        </Badge>
      </div>

      <div className="flex items-start gap-3 rounded-md border border-sky-200 bg-sky-50 p-3" data-testid="technical-mode-banner">
        <FlaskConical className="w-5 h-5 text-sky-700 shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-sm text-sky-900 leading-relaxed">
          Il motore VSME organizza i dati inseriti nel questionario e mostra lo stato
          delle informazioni richieste dallo standard.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1.5">
          <Label>Periodo di rendicontazione</Label>
          <Select value={period?.id} onValueChange={changePeriod}>
            <SelectTrigger className="w-[200px]" data-testid="vsme-period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {periods.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`vsme-period-option-${p.year}`}>
                  Esercizio {p.year} · {p.status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Opzione di rendicontazione</Label>
          {can("periods:manage") ? (
            <Select value={overview?.reporting_option || "BASIC"} onValueChange={changeOption}>
              <SelectTrigger className="w-[280px]" data-testid="reporting-option-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BASIC" data-testid="option-basic">Opzione A — Basic Module</SelectItem>
                <SelectItem value="BASIC_COMPREHENSIVE" data-testid="option-comprehensive">Opzione B — Basic + Comprehensive</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <p className="text-sm font-medium text-slate-800 h-9 flex items-center" data-testid="reporting-option-readonly">
              {overview?.reporting_option === "BASIC_COMPREHENSIVE" ? "Opzione B — Basic + Comprehensive" : "Opzione A — Basic Module"}
            </p>
          )}
        </div>
      </div>

      {overview && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-slate-200" data-testid="vsme-overall-card">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-600">Completamento complessivo</CardTitle></CardHeader>
              <CardContent>
                <CompletionBar value={overview.overall.completion} testid="overall-completion-bar" />
                <p className="text-xs text-slate-600 mt-2">
                  {overview.overall.completed} di {overview.overall.applicable} datapoint applicabili compilati ·{" "}
                  {overview.overall.missing} mancanti · {overview.overall.to_verify} da verificare · {overview.overall.approved} approvati
                </p>
              </CardContent>
            </Card>
            <Card className="border-slate-200" data-testid="vsme-basic-card">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-600">Basic Module (B1–B11)</CardTitle></CardHeader>
              <CardContent>
                <CompletionBar value={basic.completion} testid="basic-completion-bar" />
                <p className="text-xs text-slate-600 mt-2">{basic.missing} mancanti · {basic.not_applicable} non applicabili</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200" data-testid="vsme-comprehensive-card">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-600">Comprehensive Module (C1–C9)</CardTitle></CardHeader>
              <CardContent>
                {overview.comprehensive_active ? (
                  <>
                    <CompletionBar value={comp.completion} testid="comprehensive-completion-bar" />
                    <p className="text-xs text-slate-600 mt-2">{comp.missing} mancanti · {comp.not_applicable} non applicabili</p>
                  </>
                ) : (
                  <div data-testid="comprehensive-inactive">
                    <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-300">NON ATTIVO</Badge>
                    <p className="text-xs text-slate-600 mt-2">L'azienda rendiconta con il solo Basic Module.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="vsme-categories">
            {Object.entries(overview.categories).map(([cat, s]) => (
              <Card key={cat} className="border-slate-200" data-testid={`category-card-${cat.toLowerCase()}`}>
                <CardContent className="p-4">
                  <p className="text-xs font-semibold text-slate-600 mb-2">{CATEGORY_LABELS[cat]}</p>
                  <CompletionBar value={s.completion} testid={`category-${cat.toLowerCase()}-bar`} />
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-4 flex flex-wrap items-end gap-3" data-testid="vsme-filters">
              <div className="space-y-1">
                <Label className="text-xs">Modulo</Label>
                <Select value={filters.module} onValueChange={(v) => setFilters({ ...filters, module: v })}>
                  <SelectTrigger className="w-[170px] h-9" data-testid="filter-module"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Tutti</SelectItem>
                    <SelectItem value="BASIC">Basic</SelectItem>
                    <SelectItem value="COMPREHENSIVE">Comprehensive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Area ESG</Label>
                <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
                  <SelectTrigger className="w-[190px] h-9" data-testid="filter-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Tutte</SelectItem>
                    {Object.entries(CATEGORY_LABELS).map(([k, l]) => (
                      <SelectItem key={k} value={k}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Stato</Label>
                <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                  <SelectTrigger className="w-[190px] h-9" data-testid="filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Tutti</SelectItem>
                    <SelectItem value="MISSING">Con dati mancanti</SelectItem>
                    <SelectItem value="TO_VERIFY">Con dati da verificare</SelectItem>
                    <SelectItem value="COMPLETE">Complete</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 flex-1 min-w-[200px]">
                <Label className="text-xs">Ricerca</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" aria-hidden="true" />
                  <Input className="pl-8 h-9" placeholder="Es. B3, energia, workforce…" value={filters.q}
                    onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="vsme-search-input" />
                </div>
              </div>
            </CardContent>
          </Card>

          {["BASIC", "COMPREHENSIVE"].map((mod) => {
            const list = filtered.filter((d) => d.module === mod);
            if (!list.length) return null;
            return (
              <div key={mod} className="space-y-3" data-testid={`module-section-${mod.toLowerCase()}`}>
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  {MODULE_LABELS[mod]}
                  {mod === "COMPREHENSIVE" && !overview.comprehensive_active && (
                    <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-300">NON ATTIVO</Badge>
                  )}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {list.map((d) => (
                    <Link key={d.code} to={`/vsme/disclosure/${d.code}`} data-testid={`disclosure-card-${d.code.toLowerCase()}`}>
                      <Card className="border-slate-200 hover:border-green-400 hover:shadow-sm transition-all duration-150 h-full">
                        <CardContent className="p-5 space-y-3">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-bold text-slate-900">{d.code}</p>
                              <p className="text-sm text-slate-700 leading-snug">{d.title}</p>
                            </div>
                            <ArrowRight className="w-4 h-4 text-slate-400 shrink-0 mt-1" aria-hidden="true" />
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            <Badge variant="outline" className={`text-[11px] ${CATEGORY_STYLES[d.category]}`}>
                              {CATEGORY_LABELS[d.category]}
                            </Badge>
                            {!d.in_scope && (
                              <Badge variant="outline" className="text-[11px] bg-slate-100 text-slate-500 border-slate-200">Fuori perimetro</Badge>
                            )}
                          </div>
                          <CompletionBar value={d.completion} testid={`disclosure-${d.code.toLowerCase()}-bar`} />
                          <p className="text-xs text-slate-600">
                            {d.missing} mancanti · {d.to_verify} da verificare · {d.not_applicable} non applicabili
                            {d.to_determine > 0 && ` · ${d.to_determine} da determinare`}
                          </p>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
